import React, { useState } from 'react';
import { Box } from '@mui/material';
import { TableWithCustomRender } from '../../components/molecules/TableWithCustomRender';
import { BasicButton, Typography, IconButton } from '../../components/atoms';
import { useAppSelector, useAppDispatch } from '../../common/hooks';
import { useTranslation } from 'react-i18next';
import { HeaderIcon } from './HeaderIcon';
import { ReactComponent as AccountIcon } from '../../assets/icons/account.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/editIcon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/deleteIcon.svg';
import { Card } from '../../components/molecules/Card/Card';
import { CLIENT_PROFILE_STAGE_KEYS, FEATURE_NAMES, PROFILE_DETAILS_MANDATORY_KEYS } from '../../common/constants';
import { isStageDisabled } from '../../common/helper';
import { saveClient } from '../../features/client';
import { updateGoal } from '../../features/client-goals';
import { ConfirmationCentered } from '../../components/molecules/ConfirmationDialog/ConfirmationDialog';
import {
    Account,
    AdviceClient,
    AdviceGoals,
    ClientRowData,
    PersonalInfoCardFFProps,
    GoalTypes,
    TargetDateKeys
} from '../../common/types/clients-entity';
import { checkFFEnabled } from '../../utils';
import { determineLastName, isRecordDataValid, normalizeAge } from '../../features/advisorConsole/utils';
import dayjs from 'dayjs';

export const PersonalInfoCardFF: React.FC<PersonalInfoCardFFProps> = ({ setOpenDrawer, setIsRelationship }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const { globalConfig } = useAppSelector((state) => state.global);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isClientAvailable = !!adviceClient?.clientFirstName;
    const isCoClientAvailable = !!adviceClient?.relationship[0]?.firstName;
    const isCustomerIdFeatureEnabled = checkFFEnabled(FEATURE_NAMES['CUSTOMER_ID_FEATURE_AEX-3360_AEX-3380']);

    const headers_ff = [
        ...(isCustomerIdFeatureEnabled
            ? [
                  {
                      label: t('CUSTOMER_ID'),
                      key: 'customerId',
                      align: 'left' as const,
                      width: '340px',
                      render: (value: string) => (
                          <Typography
                              label={value || '-'}
                              color="neutral.grey.500"
                              variant="body.large"
                              sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}
                          />
                      )
                  }
              ]
            : []),
        { label: t('NAME'), key: 'name', align: 'left' as const, width: '23%' },
        {
            label: t('TYPE'),
            key: 'type',
            align: 'left' as const,
            width: '13%',
            sortable: true,
            render: (value: string) => (
                <Typography
                    label={t(value?.toUpperCase())}
                    color="neutral.grey.500"
                    variant="body.large"
                    sx={{ textAlign: 'left' }}
                />
            )
        },
        { label: t('BIRTH_DATE'), key: 'dob', align: 'center' as const, width: '13%' },
        { label: t('RETIREMENT_AGE'), key: 'retirementAge', align: 'center' as const, width: '13%' },
        { label: t('PLANNING_AGE'), key: 'planningAge', align: 'center' as const, width: '13%' },
        {
            label: t('EDIT'),
            key: 'edit',
            align: 'center' as const,
            width: '13%',
            render: (_value: any, row: ClientRowData) => (
                <Box display="flex" gap={1} justifyContent="center">
                    <IconButton
                        iconName={<EditIcon color="primary" />}
                        bordered={false}
                        onClick={() => (row.type === 'Client' ? handleClientClick() : handleCoClientClick())}
                        color="primary"
                    />
                    {row.type === 'Co-Client' && (
                        <IconButton
                            iconName={<DeleteIcon color="primary" />}
                            bordered={false}
                            onClick={() => setShowDeleteModal(true)}
                            color="primary"
                        />
                    )}
                </Box>
            )
        }
    ];

    const createData = (): ClientRowData[] => {
        const data: ClientRowData[] = [];

        if (isClientAvailable) {
            data.push({
                id: 'client',
                customerId: (adviceClient?.participant_id as string) ?? '',
                name: `${adviceClient.clientFirstName ?? ''} ${adviceClient.clientLastName ?? ''}`.trim(),
                lastName: determineLastName(adviceClient?.clientFirstName, adviceClient?.clientLastName),
                type: 'Client',
                dob: adviceClient.clientDob
                    ? dayjs(adviceClient.clientDob, 'DD/MM/YYYY').format(globalConfig.dateFormat)
                    : '-',
                retirementAge: normalizeAge(adviceClient.retirementAge, '65'),
                planningAge: normalizeAge(adviceClient.planningAge, '86')
            });
        }
        if (isCoClientAvailable) {
            const coClient = adviceClient.relationship[0];
            data.push({
                id: 'coClient',
                customerId: '',
                name: `${coClient?.firstName ?? ''} ${coClient?.lastName ?? ''}`.trim(),
                lastName: determineLastName(coClient?.firstName, coClient?.lastName),
                type: 'Co-Client',
                dob: coClient?.age ? dayjs(coClient?.age, 'DD/MM/YYYY').format(globalConfig.dateFormat) : '-',
                retirementAge: normalizeAge(coClient?.retirementAge, '65'),
                planningAge: normalizeAge(coClient?.planningAge, '86')
            });
        }
        return data;
    };

    const handleClientClick = () => {
        setIsRelationship(false);
        setOpenDrawer(true);
    };

    const handleCoClientClick = () => {
        setIsRelationship(true);
        setOpenDrawer(true);
    };

    const handleDeleteCoClient = async () => {
        try {
            const originalObject: AdviceClient = JSON.parse(JSON.stringify(adviceClient));
            const keysToRemove = ['loading', 'error'] as const;
            const updatedObject: AdviceClient = { ...originalObject };
            keysToRemove.forEach((key) => delete updatedObject[key]);
            let goalsData: AdviceGoals = JSON.parse(JSON.stringify(adviceGoals));
            const coClientId = adviceClient.relationship?.[0]?.id;

            // Step 1: Handle accounts before removing co-client
            if (
                updatedObject.accounts?.investmentsSources?.length &&
                updatedObject.accounts?.investmentsSources?.length > 0
            ) {
                updatedObject.accounts.investmentsSources = updatedObject.accounts.investmentsSources
                    .map((acc) => {
                        if (acc.accountHolder === 'joint') {
                            return { ...acc, accountHolder: 'primary' };
                        }
                        if (acc.accountHolder === coClientId) {
                            return null;
                        }
                        return acc;
                    })
                    .filter((acc): acc is Account => Boolean(acc));
            }

            // Step 2: Remove respective co-client income
            if (updatedObject.accounts?.incomeSources?.length && updatedObject.accounts?.incomeSources?.length > 0) {
                updatedObject.accounts.incomeSources = updatedObject.accounts.incomeSources
                    .map((inc) => (inc.sourceOwner === 'joint' ? { ...inc, sourceOwner: 'primary' } : inc))
                    .filter((inc) => inc.sourceOwner !== coClientId);
            }
            // Step 3: Update goal data
            goalsData.formFields = goalsData.formFields.map((goal) => {
                if (
                    goal.data.goalType === GoalTypes.Retirement &&
                    goal.data.targetDateKey === TargetDateKeys.coClient
                ) {
                    const retirementGoalData: any = goal?.data;
                    retirementGoalData.planStartRetirement = undefined;
                    retirementGoalData.targetDate = undefined;
                }
                if (goal?.data?.sources) {
                    const validInvestmentIds =
                        updatedObject.accounts?.investmentsSources?.map((a) => a.accountId) ?? [];
                    const validIncomeIds = updatedObject.accounts?.incomeSources?.map((a) => a.accountId) ?? [];

                    goal.data.sources.investmentSource =
                        goal.data.sources.investmentSource?.filter((src: { accountNumber: string }) =>
                            validInvestmentIds.includes(src.accountNumber)
                        ) ?? [];

                    goal.data.sources.incomeSource =
                        goal.data.sources.incomeSource?.filter((src: { accountNumber: string }) =>
                            validIncomeIds.includes(src.accountNumber)
                        ) ?? [];
                }
                return goal;
            });

            // Step 4: Remove co-client relationship
            updatedObject.relationship = [];

            // Step 5: Reset enablePDFGenerate flag if present
            if (goalsData.global?.enablePDFGenerate) {
                goalsData.global.enablePDFGenerate = false;
            }
            // Save client & goals
            await dispatch(saveClient(updatedObject));
            await dispatch(updateGoal(goalsData));

            console.log('Co-Client deleted successfully');
        } catch (error) {
            console.error('Error deleting Co-Client:', error);
        } finally {
            setShowDeleteModal(false);
        }
    };
    return (
        <>
            <Card>
                <div className="goe-client-stepper-card-inner">
                    <div className="goe-client-card-header-container">
                        <div className="goe-client-card-header">
                            <div className="goe-client-icon">
                                <HeaderIcon isDisabled={false} size={40} icon={<AccountIcon />} />
                            </div>
                            <div className="goe-client-card-header-content">
                                <Typography
                                    variant="headers.h4"
                                    color="semantic.text.default"
                                    label={t('CREATE_CLIENT_HEADER_TEXT')}
                                />
                                <Typography
                                    label={t('PERSONAL_INFORMATION_CLIENT_PROFILE')}
                                    color="semantic.text.muted"
                                    variant="body.small"
                                />
                            </div>
                        </div>
                        <div className="goe-client-card-header-buttons">
                            {!isClientAvailable && (
                                <BasicButton variant="outlined" sx={{ marginRight: '8px' }} onClick={handleClientClick}>
                                    {t('ADD_CLIENT_INFO')}
                                </BasicButton>
                            )}
                            {!isCoClientAvailable && (
                                <BasicButton
                                    variant="outlined"
                                    disabled={isStageDisabled(adviceClient, CLIENT_PROFILE_STAGE_KEYS[1])}
                                    onClick={handleCoClientClick}
                                >
                                    {t('ADD_COCLIENT_INFO')}
                                </BasicButton>
                            )}
                        </div>
                    </div>

                    {(isClientAvailable || isCoClientAvailable) && (
                        <TableWithCustomRender
                            headers={headers_ff}
                            dataSource={createData()}
                            conditionalRowFormat={(row) => {
                                const isValid = isRecordDataValid(row, PROFILE_DETAILS_MANDATORY_KEYS);
                                return !isValid
                                    ? {
                                          '&.MuiTableRow-root': {
                                              boxShadow: (theme: any) => `inset 0 0 0 2px ${theme.palette.error[500]}`
                                          }
                                      }
                                    : undefined;
                            }}
                        />
                    )}
                </div>
            </Card>
            {showDeleteModal && (
                <ConfirmationCentered
                    cancelButtonLabel={t('NO')}
                    confirmButtonLabel={t('YES')}
                    title={t('DELETING_COCLIENT')}
                    content={t('DELETE_COCLIENT_CONTENT')}
                    onCancellation={() => setShowDeleteModal(false)}
                    onCloseIconClick={() => setShowDeleteModal(false)}
                    onConfirmation={handleDeleteCoClient}
                    open
                />
            )}
        </>
    );
};
